import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, byColumnUrl, todayCST, OUTGOING_EMAILS_SHEET } from './config';
import { useTheme, tokens } from './theme';
import { getMealConfigForDate } from './specialDates';
import AllergyManager from './AllergyManager';
import { useDebug } from './debug';
import { makeProfileId, nowIso } from './allergies';

// Sheets + column headers (must match exactly)
const MEMBERS_SHEET = 'Members';
const BOOKINGS_SHEET = 'Sheet1';

const COL_MEMBER_NUMBER = 'memberNumber';   // Members sheet header
const COL_EMAIL = 'email';                  // Members sheet header

// Sheet1 header
const COL_BOOKING_MEMBER_NUMBER = 'memberNumber';

const isYes = (v) => String(v || '').trim().toLowerCase() === 'yes';

const partyLabel = (name) => {
  const n = String(name || '').trim();
  if (!n) return '(unknown)';
  const isChild = /\(c\)/i.test(n);
  const isGuest = /\(g\)/i.test(n);
  if (isChild) return `${n.replace(/\(c\)/gi, '').trim()} (Child)`;
  if (isGuest) return `${n.replace(/\(g\)/gi, '').trim()} (Guest)`;
  return `${n} (Member)`;
};

const groupByDate = (rows) => {
  const map = {};
  (rows || []).forEach((r) => {
    const d = r.date || '';
    if (!d) return;
    if (!map[d]) map[d] = [];
    map[d].push(r);
  });

  Object.keys(map).forEach((d) => {
    map[d].sort((a, b) => {
      const an = String(a.name || '');
      const bn = String(b.name || '');
      const aChild = /\(c\)/i.test(an);
      const bChild = /\(c\)/i.test(bn);
      const aGuest = /\(g\)/i.test(an);
      const bGuest = /\(g\)/i.test(bn);

      const score = (isMember, isGuest, isChild) =>
        (isMember ? 0 : 10) + (isGuest ? 1 : 0) + (isChild ? 2 : 0);

      const aScore = score(!aGuest && !aChild, aGuest, aChild);
      const bScore = score(!bGuest && !bChild, bGuest, bChild);
      return aScore - bScore;
    });
  });

  return map;
};

// Loose email check — good enough to avoid obvious typos
const isLikelyEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());

// Build a printable HTML summary of a season for a given member.
// Returns a string of complete HTML.
function buildActivityHtml({ memberNumber, memberName, year, rowsForYear }) {
  const byDate = groupByDate(rowsForYear);
  const dates = Object.keys(byDate).sort();

  const meals = { breakfast: 0, lunch: 0, dinner: 0 };
  const perPerson = {};
  rowsForYear.forEach((r) => {
    const nm = String(r.name || '').trim() || '(unknown)';
    if (!perPerson[nm]) perPerson[nm] = { breakfast: 0, lunch: 0, dinner: 0, total: 0 };
    if (isYes(r.breakfast)) { meals.breakfast++; perPerson[nm].breakfast++; perPerson[nm].total++; }
    if (isYes(r.lunch))     { meals.lunch++;     perPerson[nm].lunch++;     perPerson[nm].total++; }
    if (isYes(r.dinner))    { meals.dinner++;    perPerson[nm].dinner++;    perPerson[nm].total++; }
  });
  const total = meals.breakfast + meals.lunch + meals.dinner;

  const esc = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const perPersonRows = Object.entries(perPerson)
    .sort((a, b) => b[1].total - a[1].total)
    .map(
      ([nm, c]) =>
        `<tr><td>${esc(partyLabel(nm))}</td><td style="text-align:right">${c.breakfast}</td><td style="text-align:right">${c.lunch}</td><td style="text-align:right">${c.dinner}</td><td style="text-align:right;font-weight:700">${c.total}</td></tr>`
    )
    .join('');

  const dayBlocks = dates
    .map((d) => {
      const party = byDate[d] || [];
      const cfg = getMealConfigForDate(d);
      const bannerTag = cfg.banner
        ? ` <span style="background:#e0f2fe;color:#075985;padding:2px 8px;border-radius:999px;font-size:12px;font-weight:700">${esc(cfg.banner)}</span>`
        : '';
      const rows = party
        .map((r) => {
          const bits = [];
          if (isYes(r.breakfast)) bits.push('🍳 Breakfast');
          if (isYes(r.lunch)) bits.push('🥪 Lunch');
          if (isYes(r.dinner)) bits.push('🍽️ Dinner');
          return `<tr><td>${esc(partyLabel(r.name))}</td><td>${bits.join(' &middot; ') || '<span style="color:#94a3b8">No meals</span>'}</td></tr>`;
        })
        .join('');
      return `
        <div style="margin-top:16px;padding:12px;border:1px solid #e5e7eb;border-radius:10px">
          <div style="font-weight:800;font-size:16px">${esc(d)}${bannerTag}</div>
          <table style="width:100%;border-collapse:collapse;margin-top:6px">${rows}</table>
        </div>`;
    })
    .join('');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Wauhillau — ${esc(memberName)} — ${esc(year)} activity</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color:#0f172a; margin:24px; max-width:780px; }
  h1 { margin:0 0 4px 0; font-size:22px; }
  h2 { margin:20px 0 6px 0; font-size:17px; }
  .muted { color:#64748b; font-size:13px; }
  table { border-collapse:collapse; }
  td, th { padding:6px 10px; border-bottom:1px solid #eef2f7; font-size:14px; text-align:left; vertical-align:top; }
  th { background:#f8fafc; font-weight:700; }
  .card { border:1px solid #e5e7eb; border-radius:12px; padding:14px; margin-top:10px; }
  .stats { display:flex; gap:10px; flex-wrap:wrap; }
  .stat { flex:1 1 130px; background:#f8fafc; border:1px solid #e5e7eb; border-radius:10px; padding:10px; }
  .stat b { font-size:20px; display:block; }
  @media print { body { margin:12mm } }
</style>
</head>
<body>
  <h1>Wauhillau Club — Season Activity</h1>
  <div class="muted">Member: <b>${esc(memberName)}</b> &middot; Member #: <b>${esc(memberNumber)}</b> &middot; Season: <b>${esc(year)}</b></div>

  <h2>Totals</h2>
  <div class="stats">
    <div class="stat"><span class="muted">Breakfasts</span><b>${meals.breakfast}</b></div>
    <div class="stat"><span class="muted">Lunches</span><b>${meals.lunch}</b></div>
    <div class="stat"><span class="muted">Dinners</span><b>${meals.dinner}</b></div>
    <div class="stat"><span class="muted">Meals total</span><b>${total}</b></div>
    <div class="stat"><span class="muted">Days attended</span><b>${dates.length}</b></div>
  </div>

  <h2>By Person</h2>
  <div class="card">
    <table style="width:100%">
      <thead><tr><th>Name</th><th style="text-align:right">B</th><th style="text-align:right">L</th><th style="text-align:right">D</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${perPersonRows || `<tr><td colspan="5" class="muted">No rows</td></tr>`}</tbody>
    </table>
  </div>

  <h2>Day-by-Day</h2>
  ${dayBlocks || `<div class="muted">No meals recorded for ${esc(year)}.</div>`}

  <div class="muted" style="margin-top:28px">
    Generated ${esc(new Date().toLocaleString())} &middot; Wauhillau Club
  </div>
</body>
</html>`;
}

export default function MemberProfile({ authenticatedUser }) {
  const [theme] = useTheme();
  const t = tokens(theme);
  const [debug] = useDebug();

  const memberNumber = authenticatedUser?.memberNumber;
  const memberName = authenticatedUser?.name || '(unknown)';
  const pin = authenticatedUser?.pin || '';

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [email, setEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState('');

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyYear, setHistoryYear] = useState(String(new Date().getFullYear()));

  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  const loadProfile = async () => {
    if (!memberNumber) return;
    setLoadingProfile(true);
    setStatusMsg('');

    try {
      const res = await fetch(sheetUrl(MEMBERS_SHEET), { cache: 'no-store' });
      if (!res.ok) throw new Error(await res.text());
      const rows = await res.json();

      const row = (rows || []).find(
        (r) => String(r[COL_MEMBER_NUMBER] || '').trim() === String(memberNumber).trim()
      );

      if (!row) {
        setEmail('');
        setSavedEmail('');
        setStatusMsg(`No Members row found for Member Number ${String(memberNumber)}.`);
      } else {
        const em = String(row[COL_EMAIL] || '').trim();
        setEmail(em);
        setSavedEmail(em);
      }
    } catch (e) {
      console.error('MemberProfile loadProfile failed', e);
      setStatusMsg('Failed to load profile.');
      setEmail('');
      setSavedEmail('');
    } finally {
      setLoadingProfile(false);
    }
  };

  const saveProfile = async () => {
    if (!memberNumber) return;
    setSaving(true);
    setStatusMsg('');

    try {
      const emailValue = String(email || '').trim();

      const url = byColumnUrl(MEMBERS_SHEET, COL_MEMBER_NUMBER, String(memberNumber).trim());

      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            [COL_EMAIL]: emailValue,
          },
        }),
      });

      if (!res.ok) throw new Error(await res.text());

      setStatusMsg('Saved!');
      await loadProfile();
      setTimeout(() => setStatusMsg(''), 2000);
    } catch (e) {
      console.error('MemberProfile saveProfile failed', e);
      setStatusMsg(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    if (!memberNumber) return;
    setLoadingHistory(true);

    try {
      const res = await fetch(sheetUrl(BOOKINGS_SHEET), { cache: 'no-store' });
      if (!res.ok) throw new Error(await res.text());
      const rows = await res.json();

      const key = String(memberNumber || '').trim();

      const myRows = (rows || []).filter(
        (r) => String(r[COL_BOOKING_MEMBER_NUMBER] || '').trim() === key
      );

      setHistoryRows(myRows);
    } catch (e) {
      console.error('MemberProfile loadHistory failed', e);
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberNumber]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    historyRows.forEach((r) => {
      const y = (r.date || '').slice(0, 4);
      if (y) years.add(y);
    });
    return Array.from(years).sort().reverse();
  }, [historyRows]);

  const pastDays = useMemo(() => {
    const today = todayCST();

    const rowsForYear = (historyRows || [])
      .filter((r) => (r.date || '') < today)
      .filter((r) => (r.date || '').startsWith(historyYear))
      .filter((r) => isYes(r.breakfast) || isYes(r.lunch) || isYes(r.dinner));

    const byDate = groupByDate(rowsForYear);
    const dates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1));
    return { byDate, dates, rowsForYear };
  }, [historyRows, historyYear]);

  const noHistoryWarning =
    !loadingHistory &&
    historyRows.length === 0 &&
    memberNumber &&
    String(memberNumber).trim() !== '';

  // Build and download an HTML file of this member's season
  const downloadSeasonHtml = () => {
    const html = buildActivityHtml({
      memberNumber: String(memberNumber || ''),
      memberName,
      year: historyYear,
      rowsForYear: pastDays.rowsForYear,
    });
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wauhillau-activity-${memberNumber}-${historyYear}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // Queue an email of this member's season activity to themselves. The HTML
  // is composed here (same as the Download button) and dropped into the
  // shared OutgoingEmails queue. The Apps Script (sendPendingEmails) drains
  // it via SMTP2GO every 5 minutes.
  const requestEmail = async () => {
    const to = String(email || '').trim();
    if (!isLikelyEmail(to)) {
      setEmailMsg('Please enter a valid email address above and click Save first.');
      return;
    }
    if (to !== savedEmail) {
      setEmailMsg('Please Save your email first so we can be sure we send to the right address.');
      return;
    }

    setEmailSending(true);
    setEmailMsg('');

    try {
      const html = buildActivityHtml({
        memberNumber: String(memberNumber || ''),
        memberName,
        year: historyYear,
        rowsForYear: pastDays.rowsForYear,
      });

      const payload = {
        id: makeProfileId(),
        to,
        kind: `activity_${historyYear}_${memberNumber || ''}`,
        subject: `Wauhillau — Your ${historyYear} activity`,
        htmlBody: html,
        status: 'pending',
        requestedAt: nowIso(),
        sentAt: '',
        error: '',
      };

      const res = await fetch(sheetUrl(OUTGOING_EMAILS_SHEET), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: [payload] }),
      });
      if (!res.ok) throw new Error(await res.text());
      setEmailMsg(`Queued! You'll receive your ${historyYear} activity at ${to} within a few minutes.`);
      setTimeout(() => setEmailMsg(''), 8000);
    } catch (e) {
      console.error('requestEmail failed', e);
      setEmailMsg(`Email request failed: ${e?.message || e}`);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 700, color: t.text }}>
      <h2 style={{ marginTop: 0, color: t.textStrong }}>Member Profile</h2>

      <div style={{ marginBottom: 12, color: t.mutedSoft }}>
        <div><b>Name:</b> {memberName}</div>
        {debug && (
          <>
            <div><b>Member #:</b> {memberNumber}</div>
            <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>
              Login PIN: {pin ? pin : '(hidden)'} • History is keyed by <b>Member #</b>, not PIN.
            </div>
          </>
        )}
      </div>

      {/* Contact / email */}
      <div style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, background: t.cardAlt, marginBottom: 14 }}>
        <h3 style={{ marginTop: 0, color: t.textStrong }}>Contact</h3>
        {loadingProfile ? (
          <div>Loading…</div>
        ) : (
          <>
            <label style={{ display: 'block', fontSize: 13, color: t.mutedSoft, marginBottom: 4 }}>
              Email address (used for sending your season activity to yourself):
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: 8,
                border: `1px solid ${t.inputBorder}`,
                background: t.inputBg,
                color: t.inputText,
                fontSize: 14,
              }}
            />
            {email && !isLikelyEmail(email) && (
              <div style={{ marginTop: 6, fontSize: 12, color: t.danger }}>
                Hmm — that doesn't look like a valid email.
              </div>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={saveProfile}
                disabled={saving || email === savedEmail}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: 'none',
                  background: t.ok,
                  color: 'white',
                  fontWeight: 800,
                  cursor: saving || email === savedEmail ? 'not-allowed' : 'pointer',
                  opacity: saving || email === savedEmail ? 0.55 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save email'}
              </button>
              <button
                onClick={loadProfile}
                disabled={saving}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1px solid ${t.inputBorder}`,
                  background: t.inputBg,
                  color: t.text,
                  cursor: 'pointer',
                }}
              >
                Refresh
              </button>
            </div>

            {statusMsg && <div style={{ marginTop: 10, fontSize: 13 }}>{statusMsg}</div>}
          </>
        )}
      </div>

      {/* Allergies — household + guests CRUD */}
      <div style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, background: t.cardAlt }}>
        <AllergyManager
          memberNumber={String(memberNumber || '').trim()}
          memberName={memberName}
          theme={theme}
        />
      </div>

      {/* Past Meals */}
      <div style={{ marginTop: 16, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, background: t.card, color: t.text }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: t.textStrong }}>Past Meals</h3>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: t.mutedSoft }}>Year:</label>
            <select
              value={historyYear}
              onChange={(e) => setHistoryYear(e.target.value)}
              style={{ padding: '6px 8px', background: t.inputBg, color: t.inputText, border: `1px solid ${t.inputBorder}`, borderRadius: 6 }}
            >
              {(yearOptions.length ? yearOptions : [historyYear]).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={loadHistory}
              style={{ padding: '6px 10px', borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, cursor: 'pointer' }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Download / email controls */}
        {!loadingHistory && pastDays.dates.length > 0 && (
          <div style={{ marginTop: 12, padding: 10, border: `1px dashed ${t.borderStrong}`, borderRadius: 10, background: t.cardAlt }}>
            <div style={{ fontSize: 13, color: t.mutedSoft, marginBottom: 8 }}>
              Take this with you: download a printable copy, or have the {historyYear} summary emailed to yourself.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={downloadSeasonHtml}
                style={{
                  padding: '9px 12px',
                  borderRadius: 10,
                  border: `1px solid ${t.inputBorder}`,
                  background: t.inputBg,
                  color: t.text,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                ⬇ Download {historyYear} Activity (HTML)
              </button>
              <button
                onClick={requestEmail}
                disabled={emailSending || !isLikelyEmail(savedEmail)}
                title={!isLikelyEmail(savedEmail) ? 'Save a valid email in the Contact box above first.' : ''}
                style={{
                  padding: '9px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: t.accent,
                  color: 'white',
                  fontWeight: 800,
                  cursor: emailSending || !isLikelyEmail(savedEmail) ? 'not-allowed' : 'pointer',
                  opacity: emailSending || !isLikelyEmail(savedEmail) ? 0.6 : 1,
                }}
              >
                {emailSending ? 'Queuing…' : `✉ Email me my ${historyYear} Activity`}
              </button>
            </div>
            {emailMsg && <div style={{ marginTop: 8, fontSize: 13, color: t.mutedSoft }}>{emailMsg}</div>}
          </div>
        )}

        {loadingHistory ? (
          <div style={{ marginTop: 10 }}>Loading…</div>
        ) : noHistoryWarning ? (
          <div style={{ marginTop: 10, color: theme === 'dark' ? '#fbbf24' : '#b45309' }}>
            No history rows found for Member #{String(memberNumber)}.
            <div style={{ marginTop: 6, fontSize: 13, color: t.muted }}>
              This usually means that member's <b>memberNumber</b> in <code>users.js</code> doesn't match what's stored
              in Sheet1 column <code>memberNumber</code>. PIN differences are fine — history never uses PIN.
            </div>
          </div>
        ) : pastDays.dates.length === 0 ? (
          <div style={{ marginTop: 10, color: t.muted }}>No past meals found for {historyYear}.</div>
        ) : (
          <div style={{ marginTop: 10 }}>
            {pastDays.dates.map((date) => {
              const party = pastDays.byDate[date] || [];

              return (
                <div key={date} style={{ borderTop: `1px dashed ${t.border}`, paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontWeight: 900, fontSize: 16, color: t.textStrong }}>{date}</div>

                  <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                    {party.map((r) => (
                      <div
                        key={r.id || `${date}-${r.name}-${Math.random()}`}
                        style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: 10, background: t.cardAlt }}
                      >
                        <div style={{ fontWeight: 800, color: t.text }}>{partyLabel(r.name)}</div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6, fontSize: 13 }}>
                          {isYes(r.breakfast) && <span style={pillStyle(theme === 'dark' ? 'rgba(251,146,60,0.2)' : '#fff7ed', theme === 'dark' ? '#fdba74' : '#9a3412')}>🍳 Breakfast</span>}
                          {isYes(r.lunch) && <span style={pillStyle(theme === 'dark' ? 'rgba(6,182,212,0.2)' : '#ecfeff', theme === 'dark' ? '#67e8f9' : '#155e75')}>🥪 Lunch</span>}
                          {isYes(r.dinner) && <span style={pillStyle(theme === 'dark' ? 'rgba(99,102,241,0.25)' : '#eef2ff', theme === 'dark' ? '#a5b4fc' : '#3730a3')}>🍽️ Dinner</span>}
                          {!isYes(r.breakfast) && !isYes(r.lunch) && !isYes(r.dinner) && (
                            <span style={{ color: t.muted }}>No meals selected</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function pillStyle(bg, color) {
  return {
    padding: '6px 10px',
    borderRadius: 999,
    background: bg,
    border: '1px solid rgba(0,0,0,0.08)',
    color,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };
}
